import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const PT_LINK = "https://youfit.planway.com/";
const BT_LINK = "https://youfit.planway.com/";

export default function Services() {
  const { t } = useLang();
  return (
    <div className="bg-[var(--bg)]" data-testid="services-page">
      {/* HEADER */}
      <section className="pt-28 md:pt-32 pb-10 md:pb-14 border-b border-[color:var(--line-soft)]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p className="overline mb-4">{t("services.overline")}</p>
          <h1 className="font-serif font-light text-5xl sm:text-6xl text-[color:var(--ink)] tracking-tight leading-[1.05]">
            {t("services.title.a")} <em className="text-[color:var(--accent)] not-italic">{t("services.title.b")}</em>
          </h1>
          <p className="mt-6 text-[color:var(--ink-2)] max-w-2xl text-lg leading-relaxed">
            {t("services.lead")}
          </p>
        </div>
      </section>

      {/* CARDS */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Personal Training */}
            <article className="card-soft overflow-hidden" data-testid="service-pt-card">
              <img
                src="https://customer-assets.emergentagent.com/job_whole-being-coach/artifacts/ulx6tgbp_415782729_392355013236514_2514110994634288011_n%202.jpg"
                alt="Sopfie strength training — barbell squat"
                className="w-full aspect-[5/3] object-cover object-[50%_30%]"
              />
              <div className="p-8">
                <p className="overline">{t("services.pt.tag")}</p>
                <h3 className="mt-3 font-serif text-3xl text-[color:var(--ink)]">{t("services.pt.title")}</h3>
                <p className="mt-4 text-[color:var(--ink-2)] leading-relaxed">{t("services.pt.summary")}</p>
                <details className="mt-5 group" data-testid="pt-learn-more">
                  <summary className="cursor-pointer list-none inline-flex items-center gap-1.5 text-sm text-[color:var(--accent-2)] hover:text-[color:var(--accent)] transition-colors select-none">
                    <span className="group-open:hidden">{t("common.learn_more")} →</span>
                    <span className="hidden group-open:inline">{t("common.show_less")} ↑</span>
                  </summary>
                  <div className="mt-4 pt-4 border-t border-[color:var(--line-soft)] space-y-4 text-[color:var(--ink-2)] leading-relaxed text-[15px]">
                    <p className="font-serif italic text-lg text-[color:var(--ink)] leading-snug">{t("services.pt.long.intro")}</p>
                    <p>{t("services.pt.long.p1")}</p>
                    <p>{t("services.pt.long.p2")}</p>
                    <p>{t("services.pt.long.p3")}</p>
                    <p>{t("services.pt.long.p4")}</p>
                    <p>{t("services.pt.long.p5")}</p>
                    <p>{t("services.pt.long.p6")}</p>
                    <p className="font-serif italic text-lg text-[color:var(--ink)] leading-snug">{t("services.pt.long.outro")}</p>
                    <p className="text-[color:var(--ink)]">{t("services.pt.long.cta")}</p>
                  </div>
                </details>
                <div className="mt-6 flex flex-wrap gap-3">
                  <a href={PT_LINK} target="_blank" rel="noreferrer" className="btn-primary" data-testid="service-pt-cta">
                    {t("common.book_first")} <ArrowUpRight size={16} strokeWidth={1.6} />
                  </a>
                </div>
              </div>
            </article>

            {/* Body Therapy */}
            <article className="card-soft overflow-hidden md:mt-12" data-testid="service-bt-card">
              <img
                src="https://customer-assets.emergentagent.com/job_whole-being-coach/artifacts/mf7ok6u8_IMG_9233.jpg"
                alt="Sopfie body therapy session"
                className="w-full aspect-[5/3] object-cover object-[80%_15%]"
              />
              <div className="p-8">
                <p className="overline">{t("services.bt.tag")}</p>
                <h3 className="mt-3 font-serif text-3xl text-[color:var(--ink)]">{t("services.bt.title")}</h3>
                <p className="mt-4 text-[color:var(--ink-2)] leading-relaxed">{t("services.bt.summary")}</p>
                <details className="mt-5 group" data-testid="bt-learn-more">
                  <summary className="cursor-pointer list-none inline-flex items-center gap-1.5 text-sm text-[color:var(--accent-2)] hover:text-[color:var(--accent)] transition-colors select-none">
                    <span className="group-open:hidden">{t("common.learn_more")} →</span>
                    <span className="hidden group-open:inline">{t("common.show_less")} ↑</span>
                  </summary>
                  <div className="mt-4 pt-4 border-t border-[color:var(--line-soft)] space-y-4 text-[color:var(--ink-2)] leading-relaxed text-[15px]">
                    <p className="font-serif italic text-lg text-[color:var(--ink)] leading-snug">{t("services.bt.long.intro")}</p>
                    <p>{t("services.bt.long.p1")}</p>
                    <p>{t("services.bt.long.p2")}</p>
                    <p>{t("services.bt.long.p3")}</p>
                    <p className="font-serif italic text-[color:var(--ink)]">{t("services.bt.long.quote")}</p>
                    <p>{t("services.bt.long.p4")}</p>
                    <p>{t("services.bt.long.p5")}</p>
                    <p>{t("services.bt.long.p6")}</p>
                    <p className="text-[color:var(--ink)]">{t("services.bt.long.p7")}</p>
                    <p className="font-serif italic text-lg text-[color:var(--ink)] leading-snug">{t("services.bt.long.outro")}</p>
                  </div>
                </details>
                <div className="mt-6 flex flex-wrap gap-3">
                  <a href={BT_LINK} target="_blank" rel="noreferrer" className="btn-primary" data-testid="service-bt-cta">
                    {t("common.book_first")} <ArrowUpRight size={16} strokeWidth={1.6} />
                  </a>
                </div>
              </div>
            </article>
          </div>

          {/* Company training — full width, third service */}
          <article className="card-soft overflow-hidden mt-6 md:mt-10 grid md:grid-cols-12" data-testid="service-company-card">
            <div className="md:col-span-5">
              <img
                src="https://customer-assets.emergentagent.com/job_whole-being-coach/artifacts/usd8v22y_CD09BC6B-C0A0-4600-8FB8-C039B7C8832E.JPG"
                alt="Company training with Sopfie & Anthony"
                className="w-full h-full aspect-[4/5] md:aspect-auto object-cover"
              />
            </div>
            <div className="md:col-span-7 p-8 md:p-10 flex flex-col justify-center">
              <p className="overline">{t("services.company.tag")}</p>
              <h3 className="mt-3 font-serif text-3xl text-[color:var(--ink)]">{t("services.company.title")}</h3>
              <p className="mt-4 text-[color:var(--ink-2)] leading-relaxed">{t("services.company.body")}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/company-training" className="btn-primary" data-testid="service-company-learn-more">
                  {t("common.learn_more")} <ArrowUpRight size={16} strokeWidth={1.6} />
                </Link>
              </div>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
}
